import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Crown, BookOpen, Clock, Users, Star, ArrowRight,
  Loader2, Lock, Sparkles, Award, ChevronRight, HelpCircle
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PremiumCourses = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPremiumCourses();
  }, []);

  const fetchPremiumCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses?tier=premium`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load premium courses');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="premium-courses-page">
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 via-slate-950 to-slate-950" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-6">
            <Crown className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 text-sm font-medium">Premium Collection</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
            Master-Level AI Courses
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            In-depth, comprehensive programs designed for professionals ready to transform
            their business with AI. Expert instruction, real-world projects, and certification.
          </p>

          <div className="flex items-center justify-center gap-8 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-amber-400" /> Comprehensive Curriculum
            </span>
            <span className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-amber-400" /> Quizzes & Assessments
            </span>
            <span className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-400" /> Certificate of Completion
            </span>
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-20">
            <Crown className="w-12 h-12 mx-auto mb-4 text-amber-500/40" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">Premium courses coming soon</h3>
            <p className="text-slate-500">We're preparing exclusive content for you. Check back shortly.</p>
          </div>
        ) : (
          <div className="grid gap-8">
            {courses.map((course) => {
              const lessonCount = course.lessons?.length || 0;
              const quizCount = course.lessons?.filter(l =>
                l.has_quiz || l.quiz_count > 0
              ).length || 0;

              return (
                <Card
                  key={course.id}
                  className="bg-slate-900/60 border-slate-800/60 backdrop-blur-sm overflow-hidden hover:border-amber-500/30 transition-all duration-300 group"
                  data-testid={`premium-course-${course.id}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">
                      {/* Thumbnail */}
                      <div className="lg:w-80 h-48 lg:h-auto relative shrink-0 overflow-hidden">
                        {course.thumbnail_url ? (
                          <img
                            src={course.thumbnail_url}
                            alt={course.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-amber-900/40 to-slate-900 flex items-center justify-center">
                            <Crown className="w-16 h-16 text-amber-500/30" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-amber-500 text-black font-semibold text-xs px-3 py-1">
                            <Crown className="w-3 h-3 mr-1" /> PREMIUM
                          </Badge>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-6 lg:p-8 flex flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                              <h2 className="font-heading text-xl lg:text-2xl font-bold text-white group-hover:text-amber-300 transition-colors">
                                {course.title}
                              </h2>
                              <p className="text-sm text-slate-500 mt-1">
                                by {course.instructor_name || 'AISkillFlowPro'}
                              </p>
                            </div>
                            <ShareButtons title={course.title} description={course.description} />
                          </div>

                          <p className="text-slate-400 text-sm leading-relaxed mb-4 line-clamp-2">
                            {course.description}
                          </p>

                          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-4">
                            <span className="flex items-center gap-1.5">
                              <BookOpen className="w-3.5 h-3.5 text-amber-400/60" /> {lessonCount} lessons
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-amber-400/60" /> {course.total_duration_minutes || 0} min
                            </span>
                            {course.students_count > 0 && (
                              <span className="flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5 text-amber-400/60" /> {course.students_count} students
                              </span>
                            )}
                            <span className="capitalize flex items-center gap-1.5">
                              <Star className="w-3.5 h-3.5 text-amber-400/60" /> {course.level || 'beginner'}
                            </span>
                          </div>

                          {/* What you'll learn preview */}
                          {course.learning_outcomes?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {course.learning_outcomes.slice(0, 3).map((outcome, i) => (
                                <Badge key={i} variant="outline" className="text-xs text-slate-400 border-slate-700 font-normal">
                                  {outcome.length > 50 ? outcome.substring(0, 50) + '...' : outcome}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-slate-800/60">
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-amber-400">${course.price || 0}</span>
                            {course.lesson_price && (
                              <span className="text-xs text-slate-500">or ${course.lesson_price}/lesson</span>
                            )}
                          </div>
                          <Button
                            onClick={() => navigate(`/courses/${course.id}`)}
                            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6"
                            data-testid={`premium-enroll-${course.id}`}
                          >
                            View Course <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumCourses;
